const smallViewportHeightCache = { innerHeight: -1, height: 0 };

function smallViewportHeight(): number {
    if (smallViewportHeightCache.innerHeight === window.innerHeight) {
        return smallViewportHeightCache.height;
    }
    const probe = document.createElement("div");
    probe.style.cssText =
        "position:fixed;top:0;left:0;width:0;height:100svh;visibility:hidden;pointer-events:none";
    document.body.appendChild(probe);
    const height = probe.getBoundingClientRect().height;
    probe.remove();
    smallViewportHeightCache.innerHeight = window.innerHeight;
    smallViewportHeightCache.height = height || window.innerHeight;
    return smallViewportHeightCache.height;
}

const EXPANDED_LINE_CLAMP = "unset";

function isLineClampExpanded(element: HTMLElement): boolean {
    return (
        element.style.getPropertyValue("-webkit-line-clamp") ===
        EXPANDED_LINE_CLAMP
    );
}

function setLineClamp(element: HTMLElement, value: string) {
    if (element.style.getPropertyValue("-webkit-line-clamp") === value) return;
    element.style.setProperty("-webkit-line-clamp", value);
}

function resolveClampLines(
    element: HTMLElement,
    lines: number,
    fraction: number | undefined,
): number {
    if (!fraction) return lines;

    const lineHeight = Number.parseFloat(getComputedStyle(element).lineHeight);

    if (!Number.isFinite(lineHeight) || lineHeight <= 0) return lines;

    const targetHeight = smallViewportHeight() * fraction;
    const clampLines = Math.max(lines, Math.floor(targetHeight / lineHeight));

    setLineClamp(element, String(clampLines));
    const renderedHeight = element.clientHeight;
    if (renderedHeight <= targetHeight) return clampLines;

    return Math.max(
        lines,
        Math.floor((clampLines * targetHeight) / renderedHeight),
    );
}

function observeClampOverflow(
    element: HTMLElement,
    lines: number,
    fraction: number | undefined,
    onMeasure: (clampLines: number, isOverflowing: boolean) => void,
): () => void {
    let isCancelled = false;
    let isAwaitingFonts = false;
    let measuredWidth = 0;
    let clampedSize = "";
    let naturalSize = "";
    let reobserveFrame: number | null = null;
    let resizeFrame: number | null = null;

    const sizeSignature = () =>
        `${element.clientHeight}:${element.scrollHeight}`;

    const measure = () => {
        if (isCancelled || measuredWidth === 0) return;
        const isExpanded = isLineClampExpanded(element);
        const clampLines = resolveClampLines(element, lines, fraction);
        setLineClamp(element, String(clampLines));
        const clampedHeight = element.clientHeight;
        clampedSize = sizeSignature();
        setLineClamp(element, EXPANDED_LINE_CLAMP);
        const naturalHeight = element.scrollHeight;
        naturalSize = sizeSignature();
        if (!isExpanded) setLineClamp(element, String(clampLines));
        onMeasure(clampLines, naturalHeight > clampedHeight + 1);
    };

    const measureWithFonts = () => {
        measure();
        if (!isAwaitingFonts && document.fonts?.status === "loading") {
            isAwaitingFonts = true;
            void document.fonts.ready.then(() => {
                isAwaitingFonts = false;
                measure();
            });
        }
    };

    const observer = new ResizeObserver(() => {
        const width = element.clientWidth;
        const expectedSize = isLineClampExpanded(element)
            ? naturalSize
            : clampedSize;
        if (width === measuredWidth && sizeSignature() === expectedSize) return;
        measuredWidth = width;
        if (width === 0) return;
        observer.unobserve(element);
        measureWithFonts();
        reobserveFrame = window.requestAnimationFrame(() => {
            reobserveFrame = null;
            observer.observe(element);
        });
    });
    measuredWidth = element.clientWidth;
    measureWithFonts();
    observer.observe(element);

    const onResize = () => {
        if (resizeFrame !== null) return;
        resizeFrame = window.requestAnimationFrame(() => {
            resizeFrame = null;
            measure();
        });
    };
    if (fraction) window.addEventListener("resize", onResize);

    return () => {
        isCancelled = true;
        observer.disconnect();
        if (reobserveFrame !== null)
            window.cancelAnimationFrame(reobserveFrame);
        if (resizeFrame !== null) window.cancelAnimationFrame(resizeFrame);
        window.removeEventListener("resize", onResize);
    };
}

export { EXPANDED_LINE_CLAMP, observeClampOverflow };
